import React from 'react';
import { Video, Play, ExternalLink, Mail, Smartphone, Sparkles, BookOpen, Cross, Youtube } from 'lucide-react';
import ContentAccordion from '../components/common/ContentAccordion';
import { retreatContent } from '../data/retreatContent';

const weekThemes = {
  1: { color: 'rose', gradient: 'from-rose-500 to-rose-600', bg: 'from-rose-50 to-rose-100', border: 'border-rose-200', text: 'text-rose-700' },
  2: { color: 'amber', gradient: 'from-amber-500 to-amber-600', bg: 'from-amber-50 to-amber-100', border: 'border-amber-200', text: 'text-amber-700' },
  3: { color: 'teal', gradient: 'from-teal-500 to-teal-600', bg: 'from-teal-50 to-teal-100', border: 'border-teal-200', text: 'text-teal-700' },
  4: { color: 'violet', gradient: 'from-violet-500 to-violet-600', bg: 'from-violet-50 to-violet-100', border: 'border-violet-200', text: 'text-violet-700' }
};

const TalkItem = ({ talk, weekNum }) => {
  const url = talk.link || `https://www.youtube.com/watch?v=${talk.youtubeId}`;
  const theme = weekThemes[weekNum];
  
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-start gap-3 p-4 bg-gradient-to-r ${theme.bg} rounded-xl ${theme.border} border hover:shadow-md transition-all`}
    >
      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center flex-shrink-0 shadow`}>
        <Video className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-800 mb-0.5">{talk.title}</h4>
        <p className={`text-sm ${theme.text} font-medium mb-1`}>{talk.speaker}</p>
        <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">{talk.description}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className={`text-xs ${theme.text} bg-white/50 px-2 py-0.5 rounded-full`}>{talk.duration}</span>
          <Youtube className="w-3 h-3 text-gray-400" />
        </div>
      </div>
      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
    </a>
  );
};

const TalksPage = () => {
  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-[var(--primary)] via-[var(--primary)] to-[var(--primary-dark)] rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-center mb-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
              <Video className="w-10 h-10" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-[var(--secondary)] flex items-center justify-center">
              <BookOpen className="w-4 h-4" />
            </div>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-bold text-center mb-1">
          {retreatContent.spiritualTalks.title}
        </h1>
        <p className="text-white/80 text-sm text-center">{retreatContent.spiritualTalks.subtitle}</p>
        <div className="flex items-center justify-center gap-2 mt-4 text-white/60 text-xs">
          <Cross className="w-3 h-3" />
          <span>12 Weekly Retreat Guides</span>
        </div>
      </div>

      {/* Intro Card */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-gray-700 text-sm leading-relaxed">
            {retreatContent.spiritualTalks.intro}
          </p>
        </div>
      </div>

      {/* Weeks */}
      <div className="space-y-4">
        {retreatContent.spiritualTalks.weeks.map((weekData) => {
          const theme = weekThemes[weekData.weekNumber];
          return (
            <div key={weekData.weekNumber} className="space-y-2">
              <div className={`flex items-center gap-2 px-1`}>
                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${theme.gradient}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${theme.text}`}>
                  Week {weekData.weekNumber}
                </span>
              </div>
              <ContentAccordion
                title={weekData.theme}
                summary={weekData.description}
                defaultOpen={weekData.weekNumber === 1}
              >
                <div className="space-y-3">
                  {weekData.talks.map((talk, idx) => (
                    <TalkItem key={idx} talk={talk} weekNum={weekData.weekNumber} />
                  ))}
                </div>
              </ContentAccordion>
            </div>
          );
        })}
      </div>

      {/* Regnum Christi Daily Meditations */}
      <div className="bg-gradient-to-br from-[#1B93A4]/10 to-[#1B93A4]/20 rounded-2xl p-6 border border-[#1B93A4]/30">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1B93A4] to-[#157a8a] flex items-center justify-center shadow-lg">
            <Cross className="w-8 h-8 text-white" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-[#1B93A4] text-lg">
              Regnum Christi Daily Lenten Meditations
            </h3>
            <p className="text-[#1B93A4]/80 text-sm">Brief, power-packed reflections</p>
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-5 leading-relaxed">
          Stirring meditations to guide you in the way of mental prayer — the prayer of the saints. These brief, power-packed reflections help you structure your prayer and grow in faith.
        </p>
        <div className="flex flex-col gap-3">
          <a 
            href="https://rcspirituality.org/lent/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-white px-4 py-3 rounded-full text-sm font-bold hover:shadow-md transition-all"
          >
            <Mail className="w-4 h-4" />
            Subscribe to Daily Reflections
          </a>
          <a 
            href="https://rcspirituality.org/app/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#1B93A4] to-[#157a8a] text-white px-4 py-3 rounded-full text-sm font-bold hover:shadow-md transition-all"
          >
            <Smartphone className="w-4 h-4" />
            Get the RC Spirituality App
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center pt-2">
        <p className="text-xs text-gray-400 italic">
          "To know Jesus is to love Him." — St. Teresa of Calcutta
        </p>
      </div>
    </div>
  );
};

export default TalksPage;
